import React, { useEffect, useMemo, useState } from 'react';
import api from '@/config/axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { Alert, Button, Card, Spinner } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';
import { Activity, Calendar, Users } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';

const DEFAULT_FORM = {
  investedAmountUSDT: 0,
  investedAmountAED: 0,
  aedConversionRate: 3.662,
  simulateAdditionalUSDT: 0,
  simulateReductionUSDT: 0,
  brokerAvailable: true,
  roiMin: 0,
  roiMax: 0,
  sampledROI: 0,
  brokerMinROI: 0,
  brokerMaxROI: 0,
  startDate: '',
  endDate: new Date().toISOString().split('T')[0],
};

const formatNumber = (value, digits = 4) => Number(value || 0).toFixed(digits);
const toInputDate = (value) => (value ? new Date(value).toISOString().split('T')[0] : '');

const parseNum = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundCurrency = (value) => Number(parseNum(value).toFixed(2));
const roundPercentage = (value) => Number(parseNum(value).toFixed(4));
const formatDateLabel = (date) => new Date(date).toISOString().split('T')[0];

const calculateMovingROI = (roiMin, roiMax, dayIndex) => {
  const minVal = parseNum(roiMin);
  const maxVal = parseNum(roiMax);
  const avg = (minVal + maxVal) / 2;
  const amplitude = (maxVal - minVal) / 2;
  const cycle = 30;
  const angle = ((dayIndex % cycle) / cycle) * Math.PI * 2;
  return avg + amplitude * Math.sin(angle);
};

function AdminROIDebug() {
  const { user } = useAuth();
  const { error: showError, success: showSuccess } = useToastContext();

  const [loading, setLoading] = useState(true);
  const [investors, setInvestors] = useState([]);
  const [selectedInvestorId, setSelectedInvestorId] = useState('');
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [historicalStats, setHistoricalStats] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);

  const selectedInvestorLabel = useMemo(() => {
    if (!selectedInvestor) return 'No investor selected';
    return `${selectedInvestor.firstName || ''} ${selectedInvestor.lastName || ''}`.trim() || selectedInvestor.email || 'Selected investor';
  }, [selectedInvestor]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      setLoading(false);
      return;
    }
    fetchInvestors();
  }, [user?.role]);

  const fetchInvestors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users?role=investor&limit=500');
      const investorList = (response.data?.users || []).filter((u) => u.role === 'investor');
      setInvestors(investorList);
    } catch (error) {
      console.error('Error fetching investors:', error);
      showError('Failed to fetch investors');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrokerRelationship = async (investorId) => {
    try {
      const response = await api.get('/api/broker-investors', {
        params: { investor_id: investorId, limit: 1 },
      });
      return response.data?.brokerInvestors?.[0] || null;
    } catch (error) {
      console.error('Error fetching broker relationship:', error);
      return null;
    }
  };

  const fetchHistoricalStats = async (investorId, date) => {
    try {
      const response = await api.get(`/api/minute-roi/daily-average/${investorId}`, {
        params: { date },
      });
      const data = response.data?.data;
      if (!data) {
        setHistoricalStats(null);
        return;
      }
      setHistoricalStats({
        date,
        avgROI: Number(data.avgROI || 0),
        minROI: Number(data.minROI || 0),
        maxROI: Number(data.maxROI || 0),
        totalProfit: Number(data.totalProfit || 0),
        dataPoints: Number(data.count || 0),
      });
    } catch (error) {
      // Not all dates will have minute ROI data. Keep this non-blocking.
      setHistoricalStats(null);
    }
  };

  const handleInvestorSelect = async (investorId) => {
    setSelectedInvestorId(investorId);
    setResult(null);

    if (!investorId) {
      setSelectedInvestor(null);
      setHistoricalStats(null);
      setForm(DEFAULT_FORM);
      return;
    }

    const investor = investors.find((item) => item._id === investorId);
    if (!investor) return;

    setSelectedInvestor(investor);

    const relationship = await fetchBrokerRelationship(investorId);

    const roiMin = Number(investor.roi_min || 0);
    const roiMax = Number(investor.roi_max || 0);
    const avgROI = (roiMin + roiMax) / 2;

    setForm({
      investedAmountUSDT: Number(investor.invested_amount || 0),
      investedAmountAED: Number(investor.invested_amount_aed || investor.amount_in_aed || 0),
      aedConversionRate: Number(investor.aed_conversion_rate || 3.662),
      simulateAdditionalUSDT: 0,
      simulateReductionUSDT: 0,
      brokerAvailable: !!relationship,
      roiMin,
      roiMax,
      sampledROI: avgROI,
      brokerMinROI: Number(
        relationship?.broker_min_roi ?? investor?.broker_min_roi ?? roiMin ?? 0
      ),
      brokerMaxROI: Number(
        relationship?.broker_max_roi ?? investor?.broker_max_roi ?? roiMax ?? 0
      ),
      startDate: toInputDate(investor.startDate || investor.createdAt || investor.created_at),
      endDate: new Date().toISOString().split('T')[0],
    });

    await fetchHistoricalStats(investorId, new Date().toISOString().split('T')[0]);
  };

  const handleFormChange = (key, value) => {
    setResult(null);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleUSDTBlur = () => {
    const usdt = parseNum(form.investedAmountUSDT);
    const rate = parseNum(form.aedConversionRate);
    if (rate <= 0) return;

    const convertedAED = roundCurrency(usdt * rate);
    setResult(null);
    setForm((prev) => ({
      ...prev,
      investedAmountUSDT: roundCurrency(usdt),
      investedAmountAED: convertedAED,
    }));
  };

  const handleAEDBlur = () => {
    const aed = parseNum(form.investedAmountAED);
    const rate = parseNum(form.aedConversionRate);
    if (rate <= 0) return;

    const convertedUSDT = roundCurrency(aed / rate);
    setResult(null);
    setForm((prev) => ({
      ...prev,
      investedAmountAED: roundCurrency(aed),
      investedAmountUSDT: convertedUSDT,
    }));
  };

  const handleRateBlur = () => {
    const rate = parseNum(form.aedConversionRate);
    if (rate <= 0) return;

    // Recompute AED using the current USDT value whenever rate is edited.
    const usdt = parseNum(form.investedAmountUSDT);
    const convertedAED = roundCurrency(usdt * rate);
    setResult(null);
    setForm((prev) => ({
      ...prev,
      aedConversionRate: Number(rate.toFixed(4)),
      investedAmountAED: convertedAED,
    }));
  };

  const handleROIRangeBlur = () => {
    const roiMin = roundPercentage(form.roiMin);
    const roiMax = roundPercentage(form.roiMax);
    const safeMin = Math.min(roiMin, roiMax);
    const safeMax = Math.max(roiMin, roiMax);
    const sampled = roundPercentage((safeMin + safeMax) / 2);

    setResult(null);
    setForm((prev) => ({
      ...prev,
      roiMin: safeMin,
      roiMax: safeMax,
      sampledROI: sampled,
    }));
  };

  const handleSampledROIBlur = () => {
    const roiMin = roundPercentage(form.roiMin);
    const roiMax = roundPercentage(form.roiMax);
    const avgFromRange = roundPercentage((Math.min(roiMin, roiMax) + Math.max(roiMin, roiMax)) / 2);
    const sampled = parseNum(form.sampledROI);
    const normalizedSampled = sampled > 0 ? roundPercentage(sampled) : avgFromRange;

    setResult(null);
    setForm((prev) => ({
      ...prev,
      sampledROI: normalizedSampled,
    }));
  };

  const handleRecheckHistorical = async () => {
    if (!selectedInvestorId) return;
    await fetchHistoricalStats(selectedInvestorId, form.endDate);
  };

  const handleExportXLSX = () => {
    if (!result || !selectedInvestor) {
      showError('Run calculation first, then export');
      return;
    }

    const summaryRows = [
      {
        metric: 'Investor',
        value: `${selectedInvestor.firstName || ''} ${selectedInvestor.lastName || ''}`.trim() || selectedInvestor.email || '',
      },
      { metric: 'Investor Email', value: selectedInvestor.email || '' },
      { metric: 'Broker Available', value: result.investor.brokerAvailable ? 'Yes' : 'No' },
      { metric: 'Start Date', value: form.startDate || '' },
      { metric: 'End Date', value: form.endDate || '' },
      { metric: 'Total Active Days', value: result.constants.activeDays },
      { metric: 'Base Investment USDT', value: Number(formatNumber(result.investor.investedAmountUSDT, 2)) },
      { metric: 'Simulate Additional Investment USDT', value: Number(formatNumber(result.investor.simulateAdditionalUSDT, 2)) },
      { metric: 'Simulate Reduction Investment USDT', value: Number(formatNumber(result.investor.simulateReductionUSDT, 2)) },
      { metric: 'Effective Investment USDT (After Simulation)', value: Number(formatNumber(result.investor.effectiveInvestedUSDT, 2)) },
      { metric: 'Investment Base AED', value: Number(formatNumber(result.investor.investedAmountAED, 2)) },
      { metric: 'Avg ROI %', value: Number(formatNumber(result.investor.avgROI, 4)) },
      { metric: 'Effective/Sampled ROI %', value: Number(formatNumber(result.investor.effectiveROI, 4)) },
      { metric: 'Investor Daily Profit AED', value: Number(formatNumber(result.investorProfit.daily, 4)) },
      { metric: 'Investor Monthly Profit AED', value: Number(formatNumber(result.investorProfit.monthly, 4)) },
      { metric: 'Broker Daily Commission AED', value: Number(formatNumber(result.brokerCommission.daily, 4)) },
      { metric: 'Broker Monthly Commission AED', value: Number(formatNumber(result.brokerCommission.monthly, 4)) },
      { metric: 'Investor Net Daily AED', value: Number(formatNumber(result.net.daily, 4)) },
      { metric: 'Investor Net Monthly AED', value: Number(formatNumber(result.net.monthly, 4)) },
      { metric: 'Profit Till Date AED', value: Number(formatNumber(result.tillDate.profit, 2)) },
      { metric: 'Commission Till Date AED', value: Number(formatNumber(result.tillDate.commission, 2)) },
      { metric: 'Projected Gross Since Start AED', value: Number(formatNumber(result.historicalProjection.gross, 2)) },
      { metric: 'Projected Commission Since Start AED', value: Number(formatNumber(result.historicalProjection.commission, 2)) },
      { metric: 'Projected Net Since Start AED', value: Number(formatNumber(result.historicalProjection.net, 2)) },
    ];

    const dailyRows = result.dailyBreakdown.map((row) => {
      const base = {
        day: row.dayNumber,
        date: row.date,
        moving_roi_percent: Number(formatNumber(row.movingROI, 4)),
        daily_profit_aed: Number(formatNumber(row.dailyProfit, 4)),
        change_vs_prev_day_aed: Number(formatNumber(row.changeVsPreviousDay, 4)),
        deflection_vs_avg_roi_aed: Number(formatNumber(row.deflectionVsAvgROI, 4)),
        net_daily_profit_aed: Number(formatNumber(row.netDailyProfit, 4)),
        cumulative_profit_aed: Number(formatNumber(row.cumulativeProfit, 2)),
        cumulative_net_aed: Number(formatNumber(row.cumulativeNet, 2)),
      };

      if (result.investor.brokerAvailable) {
        return {
          ...base,
          daily_commission_aed: Number(formatNumber(row.dailyCommission, 4)),
          cumulative_commission_aed: Number(formatNumber(row.cumulativeCommission, 2)),
        };
      }
      return base;
    });

    const formulaNotesRows = [
      {
        step: 0,
        formula: 'Investment base (AED)',
        expression: result.investor.usingDirectAED
          ? `Direct AED used = ${formatNumber(result.investor.investedAmountAEDInput, 2)}`
          : `USDT * conversion rate = ${formatNumber(result.investor.investedAmountUSDT, 2)} * ${formatNumber(result.investor.aedConversionRate, 4)}`,
        result: formatNumber(result.investor.investedAmountAED, 2),
      },
      {
        step: 0.1,
        formula: 'Simulation effective USDT',
        expression: `base USDT + additional - reduction = ${formatNumber(result.investor.investedAmountUSDT, 2)} + ${formatNumber(result.investor.simulateAdditionalUSDT, 2)} - ${formatNumber(result.investor.simulateReductionUSDT, 2)}`,
        result: formatNumber(result.investor.effectiveInvestedUSDT, 2),
      },
      {
        step: 1,
        formula: 'Investor average ROI',
        expression: `(ROI min + ROI max) / 2 = (${formatNumber(result.investor.roiMin, 4)} + ${formatNumber(result.investor.roiMax, 4)}) / 2`,
        result: `${formatNumber(result.investor.avgROI, 4)}%`,
      },
      {
        step: 2,
        formula: 'Investor monthly profit (AED)',
        expression: `investment AED * sampled ROI / 100 = ${formatNumber(result.investor.investedAmountAED, 2)} * ${formatNumber(result.investor.effectiveROI, 4)} / 100`,
        result: formatNumber(result.investorProfit.monthly, 4),
      },
      {
        step: 3,
        formula: 'Investor daily profit (AED)',
        expression: `monthly profit / 30 = ${formatNumber(result.investorProfit.monthly, 4)} / ${result.constants.daysInMonth}`,
        result: formatNumber(result.investorProfit.daily, 4),
      },
      {
        step: 4,
        formula: 'Investor hourly profit (AED)',
        expression: `daily profit / 24 = ${formatNumber(result.investorProfit.daily, 4)} / ${result.constants.hoursInDay}`,
        result: formatNumber(result.investorProfit.hourly, 6),
      },
      {
        step: 5,
        formula: 'Investor per-minute profit (AED)',
        expression: `hourly profit / 60 = ${formatNumber(result.investorProfit.hourly, 6)} / ${result.constants.minutesInHour}`,
        result: formatNumber(result.investorProfit.perMinute, 8),
      },
      ...(result.investor.brokerAvailable
        ? [
            {
              step: 6,
              formula: 'Broker average ROI',
              expression: `(broker min ROI + broker max ROI) / 2 = (${formatNumber(result.broker.brokerMinROI, 4)} + ${formatNumber(result.broker.brokerMaxROI, 4)}) / 2`,
              result: `${formatNumber(result.broker.avgBrokerROI, 4)}%`,
            },
            {
              step: 7,
              formula: 'Broker monthly commission (AED)',
              expression: `investor monthly profit * broker avg ROI / 100 = ${formatNumber(result.investorProfit.monthly, 4)} * ${formatNumber(result.broker.avgBrokerROI, 4)} / 100`,
              result: formatNumber(result.brokerCommission.monthly, 6),
            },
            {
              step: 8,
              formula: 'Broker daily commission (AED)',
              expression: `broker monthly commission / 30 = ${formatNumber(result.brokerCommission.monthly, 6)} / ${result.constants.daysInMonth}`,
              result: formatNumber(result.brokerCommission.daily, 8),
            },
          ]
        : [
            {
              step: 6,
              formula: 'Broker commission',
              expression: 'Broker Available = No',
              result: 'Skipped (all commission values = 0)',
            },
          ]),
      {
        step: 9,
        formula: 'Net daily investor profit (AED)',
        expression: `investor daily profit - broker daily commission = ${formatNumber(result.investorProfit.daily, 6)} - ${formatNumber(result.brokerCommission.daily, 8)}`,
        result: formatNumber(result.net.daily, 6),
      },
      {
        step: 10,
        formula: 'Historical projected net (AED)',
        expression: `net daily * active days = ${formatNumber(result.net.daily, 6)} * ${result.constants.activeDays}`,
        result: formatNumber(result.historicalProjection.net, 4),
      },
      {
        step: 11,
        formula: 'Moving ROI for daily table',
        expression: 'ROI oscillates between ROI min and ROI max on a 30-day cycle',
        result: 'Used for day-by-day profit simulation',
      },
      {
        step: 12,
        formula: 'Deflection vs average ROI baseline',
        expression: 'daily profit from moving ROI - daily profit from average ROI',
        result: 'Shown in daily table column',
      },
    ];

    const workbook = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
    const dailySheet = XLSX.utils.json_to_sheet(dailyRows);
    const formulaSheet = XLSX.utils.json_to_sheet(formulaNotesRows);

    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    XLSX.utils.book_append_sheet(workbook, dailySheet, 'Daily Breakdown');
    XLSX.utils.book_append_sheet(workbook, formulaSheet, 'Formula Notes');

    const safeName = (selectedInvestorLabel || 'investor').replace(/[^a-zA-Z0-9_-]/g, '_');
    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `roi_debug_${safeName}_${today}.xlsx`);
    showSuccess('XLSX exported successfully');
  };

  const runCalculation = () => {
    if (!selectedInvestorId) {
      showError('Please select an investor first');
      return;
    }

    setCalculating(true);
    try {
      const investedAmountUSDT = parseNum(form.investedAmountUSDT);
      const investedAmountAEDInput = parseNum(form.investedAmountAED);
      const aedConversionRate = parseNum(form.aedConversionRate) || 3.662;
      const simulateAdditionalUSDT = parseNum(form.simulateAdditionalUSDT);
      const simulateReductionUSDT = parseNum(form.simulateReductionUSDT);
      const roiMin = parseNum(form.roiMin);
      const roiMax = parseNum(form.roiMax);
      const sampledROI = parseNum(form.sampledROI);
      const brokerAvailable = !!form.brokerAvailable;
      const brokerMinROI = brokerAvailable ? parseNum(form.brokerMinROI) : 0;
      const brokerMaxROI = brokerAvailable ? parseNum(form.brokerMaxROI) : 0;

      const investedAfterAdditionalUSDT = investedAmountUSDT + Math.max(0, simulateAdditionalUSDT);
      if (simulateReductionUSDT > investedAfterAdditionalUSDT) {
        showError('Simulation reduction cannot exceed current invested amount after additional investment');
        setCalculating(false);
        return;
      }
      const effectiveInvestedUSDT = Math.max(
        0,
        investedAfterAdditionalUSDT - Math.max(0, simulateReductionUSDT)
      );

      // Use AED amount directly if available; otherwise convert from USDT.
      const usingDirectAED = investedAmountAEDInput > 0;
      const baseInvestedAED = usingDirectAED
        ? investedAmountAEDInput
        : investedAmountUSDT * aedConversionRate;
      const simulationDeltaUSDT = effectiveInvestedUSDT - investedAmountUSDT;
      const investedAmountAED = Math.max(0, baseInvestedAED + (simulationDeltaUSDT * aedConversionRate));

      const avgROI = (roiMin + roiMax) / 2;
      const effectiveROI = sampledROI > 0 ? sampledROI : avgROI;
      const avgBrokerROI = (brokerMinROI + brokerMaxROI) / 2;

      const daysInMonth = 30;
      const hoursInDay = 24;
      const minutesInHour = 60;

      const monthlyInvestorProfit = (investedAmountAED * effectiveROI) / 100;
      const dailyInvestorProfit = monthlyInvestorProfit / daysInMonth;
      const hourlyInvestorProfit = dailyInvestorProfit / hoursInDay;
      const minuteInvestorProfit = hourlyInvestorProfit / minutesInHour;

      const brokerMonthlyCommission = brokerAvailable ? (monthlyInvestorProfit * avgBrokerROI) / 100 : 0;
      const brokerDailyCommission = brokerAvailable ? brokerMonthlyCommission / daysInMonth : 0;
      const brokerHourlyCommission = brokerAvailable ? brokerDailyCommission / hoursInDay : 0;
      const brokerMinuteCommission = brokerAvailable ? brokerHourlyCommission / minutesInHour : 0;

      const investorNetDaily = dailyInvestorProfit - brokerDailyCommission;
      const investorNetMonthly = monthlyInvestorProfit - brokerMonthlyCommission;

      const minMonthlyProfit = (investedAmountAED * roiMin) / 100;
      const maxMonthlyProfit = (investedAmountAED * roiMax) / 100;
      const minDailyProfit = minMonthlyProfit / daysInMonth;
      const maxDailyProfit = maxMonthlyProfit / daysInMonth;

      const endDate = form.endDate ? new Date(form.endDate) : new Date();
      const startDate = form.startDate ? new Date(form.startDate) : endDate;
      const normalizedEnd = new Date(endDate.setHours(0, 0, 0, 0));
      const normalizedStart = new Date(startDate.setHours(0, 0, 0, 0));
      const daysDiff = Math.floor((normalizedEnd - normalizedStart) / (1000 * 60 * 60 * 24));
      const activeDays = Math.max(daysDiff + 1, 0);

      const projectedHistoricalGross = dailyInvestorProfit * activeDays;
      const projectedHistoricalCommission = brokerDailyCommission * activeDays;
      const projectedHistoricalNet = projectedHistoricalGross - projectedHistoricalCommission;

      const dailyBaselineFromAvgROI = (investedAmountAED * avgROI / 100) / daysInMonth;
      const dailyBreakdown = [];
      let cumulativeProfit = 0;
      let cumulativeCommission = 0;
      let cumulativeNet = 0;
      let previousDailyProfit = null;

      for (let dayIndex = 0; dayIndex < activeDays; dayIndex++) {
        const rowDate = new Date(normalizedStart);
        rowDate.setDate(rowDate.getDate() + dayIndex);

        const movingROI = calculateMovingROI(roiMin, roiMax, dayIndex + 1);
        const dayProfit = (investedAmountAED * movingROI / 100) / daysInMonth;
        const dayCommission = brokerAvailable ? (dayProfit * avgBrokerROI) / 100 : 0;
        const dayNet = dayProfit - dayCommission;
        const changeVsPreviousDay = previousDailyProfit === null ? 0 : dayProfit - previousDailyProfit;
        const deflectionVsAvgROI = dayProfit - dailyBaselineFromAvgROI;

        cumulativeProfit += dayProfit;
        cumulativeCommission += dayCommission;
        cumulativeNet += dayNet;

        dailyBreakdown.push({
          date: formatDateLabel(rowDate),
          dayNumber: dayIndex + 1,
          movingROI,
          dailyProfit: dayProfit,
          dailyCommission: dayCommission,
          netDailyProfit: dayNet,
          changeVsPreviousDay,
          deflectionVsAvgROI,
          cumulativeProfit,
          cumulativeCommission,
          cumulativeNet,
        });

        previousDailyProfit = dayProfit;
      }

      setResult({
        constants: { daysInMonth, hoursInDay, minutesInHour, activeDays },
        investor: {
          investedAmountUSDT,
          simulateAdditionalUSDT,
          simulateReductionUSDT,
          effectiveInvestedUSDT,
          investedAmountAEDInput,
          investedAmountAED,
          aedConversionRate,
          usingDirectAED,
          brokerAvailable,
          roiMin,
          roiMax,
          avgROI,
          effectiveROI,
        },
        broker: {
          brokerMinROI,
          brokerMaxROI,
          avgBrokerROI,
        },
        investorProfit: {
          monthly: monthlyInvestorProfit,
          daily: dailyInvestorProfit,
          hourly: hourlyInvestorProfit,
          perMinute: minuteInvestorProfit,
          minDaily: minDailyProfit,
          maxDaily: maxDailyProfit,
        },
        brokerCommission: {
          monthly: brokerMonthlyCommission,
          daily: brokerDailyCommission,
          hourly: brokerHourlyCommission,
          perMinute: brokerMinuteCommission,
        },
        net: {
          daily: investorNetDaily,
          monthly: investorNetMonthly,
        },
        historicalProjection: {
          gross: projectedHistoricalGross,
          commission: projectedHistoricalCommission,
          net: projectedHistoricalNet,
        },
        tillDate: {
          profit: cumulativeProfit,
          commission: cumulativeCommission,
          net: cumulativeNet,
        },
        dailyBreakdown,
      });
    } finally {
      setCalculating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (user?.role !== 'admin') {
    return <Alert type="error">Access denied. This page is only available to admins.</Alert>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50 flex items-center gap-2">
            <Activity size={28} className="text-primary-600 dark:text-primary-400" />
            ROI Debug Calculator (Admin)
          </h1>
          <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
            Debug investor ROI, daily profit, and broker commission with full formula transparency.
          </p>
        </div>
      </div>

      <Card className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-1.5">
              <Users size={14} className="inline mr-1" />
              Investor Selection
            </label>
            <select
              value={selectedInvestorId}
              onChange={(e) => handleInvestorSelect(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900 text-primary-900 dark:text-primary-100"
            >
              <option value="">Select investor</option>
              {investors.map((investor) => (
                <option key={investor._id} value={investor._id}>
                  {investor.firstName} {investor.lastName} ({investor.email})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-1.5">
              <Calendar size={14} className="inline mr-1" />
              Debug End Date
            </label>
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => handleFormChange('endDate', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900 text-primary-900 dark:text-primary-100"
            />
          </div>
        </div>

        <div className="text-sm text-primary-700 dark:text-primary-300">
          Selected: <span className="font-semibold">{selectedInvestorLabel}</span>
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-4">Prefilled Inputs (Editable)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Invested Amount (USDT)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.investedAmountUSDT}
              onChange={(e) => handleFormChange('investedAmountUSDT', e.target.value)}
              onBlur={handleUSDTBlur}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Invested Amount (AED, if available)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.investedAmountAED}
              onChange={(e) => handleFormChange('investedAmountAED', e.target.value)}
              onBlur={handleAEDBlur}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">USDT to AED Rate</label>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={form.aedConversionRate}
              onChange={(e) => handleFormChange('aedConversionRate', e.target.value)}
              onBlur={handleRateBlur}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Simulate Additional Investment (USDT)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.simulateAdditionalUSDT}
              onChange={(e) => handleFormChange('simulateAdditionalUSDT', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Simulate Reduction Investment (USDT)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.simulateReductionUSDT}
              onChange={(e) => handleFormChange('simulateReductionUSDT', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ROI Min (%)</label>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={form.roiMin}
              onChange={(e) => handleFormChange('roiMin', e.target.value)}
              onBlur={handleROIRangeBlur}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
            />
          </div>
          <div className="flex items-center gap-2 mt-7">
            <input
              id="brokerAvailable"
              type="checkbox"
              checked={!!form.brokerAvailable}
              onChange={(e) => handleFormChange('brokerAvailable', e.target.checked)}
              className="h-4 w-4 accent-primary-600"
            />
            <label htmlFor="brokerAvailable" className="text-sm font-medium">
              Broker Available
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ROI Max (%)</label>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={form.roiMax}
              onChange={(e) => handleFormChange('roiMax', e.target.value)}
              onBlur={handleROIRangeBlur}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Sampled ROI (%)</label>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={form.sampledROI}
              onChange={(e) => handleFormChange('sampledROI', e.target.value)}
              onBlur={handleSampledROIBlur}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
            />
          </div>
          {form.brokerAvailable && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Broker Min ROI (%)</label>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={form.brokerMinROI}
                  onChange={(e) => handleFormChange('brokerMinROI', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Broker Max ROI (%)</label>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={form.brokerMaxROI}
                  onChange={(e) => handleFormChange('brokerMaxROI', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
                />
              </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Investment Start Date</label>
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => handleFormChange('startDate', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={handleRecheckHistorical} className="w-full">
              Refresh Historical ROI
            </Button>
          </div>
        </div>

        <div className="mt-5 flex gap-3">
          <Button onClick={runCalculation} disabled={!selectedInvestorId || calculating}>
            {calculating ? 'Calculating...' : 'Run Debug Calculation'}
          </Button>
          <Button variant="outline" onClick={handleExportXLSX} disabled={!result}>
            Export XLSX
          </Button>
        </div>
      </Card>

      {historicalStats && (
        <Card className="p-5">
          <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-3">
            Historical Snapshot ({historicalStats.date})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30">
              <p className="text-xs text-primary-600 dark:text-primary-300">Avg ROI</p>
              <p className="text-lg font-semibold">{formatNumber(historicalStats.avgROI, 4)}%</p>
            </div>
            <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30">
              <p className="text-xs text-primary-600 dark:text-primary-300">Min ROI</p>
              <p className="text-lg font-semibold">{formatNumber(historicalStats.minROI, 4)}%</p>
            </div>
            <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30">
              <p className="text-xs text-primary-600 dark:text-primary-300">Max ROI</p>
              <p className="text-lg font-semibold">{formatNumber(historicalStats.maxROI, 4)}%</p>
            </div>
            <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30">
              <p className="text-xs text-primary-600 dark:text-primary-300">Total Daily Profit</p>
              <p className="text-lg font-semibold flex items-center gap-1">
                <DirhamSymbol size={14} inline />
                {formatNumber(historicalStats.totalProfit, 2)}
              </p>
            </div>
          </div>
          <p className="text-xs text-primary-600 dark:text-primary-300 mt-3">
            Data points captured: {historicalStats.dataPoints}
          </p>
        </Card>
      )}

      {result && (
        <>
          <Card className="p-5">
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-3">Calculation Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/20">
                <p className="text-xs">Investment Base (AED)</p>
                <p className="text-lg font-semibold flex items-center gap-1">
                  <DirhamSymbol size={14} inline />
                  {formatNumber(result.investor.investedAmountAED, 2)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/20">
                <p className="text-xs">Effective Investment (USDT)</p>
                <p className="text-lg font-semibold">
                  {formatNumber(result.investor.effectiveInvestedUSDT, 2)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
                <p className="text-xs">Total Active Days</p>
                <p className="text-lg font-semibold">{result.constants.activeDays}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                <p className="text-xs">Avg ROI</p>
                <p className="text-lg font-semibold">{formatNumber(result.investor.avgROI, 4)}%</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                <p className="text-xs">Investor Daily Profit</p>
                <p className="text-lg font-semibold flex items-center gap-1">
                  <DirhamSymbol size={14} inline />
                  {formatNumber(result.investorProfit.daily, 4)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                <p className="text-xs">Broker Daily Commission</p>
                <p className="text-lg font-semibold flex items-center gap-1">
                  <DirhamSymbol size={14} inline />
                  {formatNumber(result.brokerCommission.daily, 4)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <p className="text-xs">Investor Net Daily</p>
                <p className="text-lg font-semibold flex items-center gap-1">
                  <DirhamSymbol size={14} inline />
                  {formatNumber(result.net.daily, 4)}
                </p>
              </div>
              {result.investor.brokerAvailable && (
                <>
                  <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                    <p className="text-xs">Profit Till Date</p>
                    <p className="text-lg font-semibold flex items-center gap-1">
                      <DirhamSymbol size={14} inline />
                      {formatNumber(result.tillDate.profit, 2)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20">
                    <p className="text-xs">Commission Till Date</p>
                    <p className="text-lg font-semibold flex items-center gap-1">
                      <DirhamSymbol size={14} inline />
                      {formatNumber(result.tillDate.commission, 2)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-3">Formula Notes (Step by Step)</h2>
            <div className="space-y-2 text-sm text-primary-800 dark:text-primary-200">
              <p>0) Investment base for calculation (AED)</p>
              {result.investor.usingDirectAED ? (
                <p>
                  Direct AED amount found, no conversion needed. Investment base = {formatNumber(result.investor.investedAmountAEDInput, 2)}
                </p>
              ) : (
                <p>
                  AED amount not available, converted from USDT: {formatNumber(result.investor.investedAmountUSDT, 2)} *{' '}
                  {formatNumber(result.investor.aedConversionRate, 4)} = {formatNumber(result.investor.investedAmountAED, 2)}
                </p>
              )}
              <p>0.1) Simulation effective investment (USDT) = base USDT + additional - reduction</p>
              <p>
                = {formatNumber(result.investor.investedAmountUSDT, 2)} + {formatNumber(result.investor.simulateAdditionalUSDT, 2)} - {formatNumber(result.investor.simulateReductionUSDT, 2)} = {formatNumber(result.investor.effectiveInvestedUSDT, 2)}
              </p>
              <p>1) Investor average ROI = (ROI min + ROI max) / 2</p>
              <p>
                = ({formatNumber(result.investor.roiMin, 4)} + {formatNumber(result.investor.roiMax, 4)}) / 2 ={' '}
                {formatNumber(result.investor.avgROI, 4)}%
              </p>
              <p>2) Investor monthly profit = invested amount (AED) * sampled ROI / 100</p>
              <p>
                = {formatNumber(result.investor.investedAmountAED, 2)} * {formatNumber(result.investor.effectiveROI, 4)} / 100 ={' '}
                {formatNumber(result.investorProfit.monthly, 4)}
              </p>
              <p>3) Investor daily profit = monthly profit / 30</p>
              <p>
                = {formatNumber(result.investorProfit.monthly, 4)} / {result.constants.daysInMonth} ={' '}
                {formatNumber(result.investorProfit.daily, 4)}
              </p>
              <p>4) Investor hourly profit = daily profit / 24</p>
              <p>
                = {formatNumber(result.investorProfit.daily, 4)} / {result.constants.hoursInDay} ={' '}
                {formatNumber(result.investorProfit.hourly, 6)}
              </p>
              <p>5) Investor per-minute profit = hourly profit / 60</p>
              <p>
                = {formatNumber(result.investorProfit.hourly, 6)} / {result.constants.minutesInHour} ={' '}
                {formatNumber(result.investorProfit.perMinute, 8)}
              </p>
              {result.investor.brokerAvailable ? (
                <>
                  <p>6) Broker average ROI = (broker min ROI + broker max ROI) / 2</p>
                  <p>
                    = ({formatNumber(result.broker.brokerMinROI, 4)} + {formatNumber(result.broker.brokerMaxROI, 4)}) / 2 ={' '}
                    {formatNumber(result.broker.avgBrokerROI, 4)}%
                  </p>
                  <p>7) Broker monthly commission = investor monthly profit * broker average ROI / 100</p>
                  <p>
                    = {formatNumber(result.investorProfit.monthly, 4)} * {formatNumber(result.broker.avgBrokerROI, 4)} / 100 ={' '}
                    {formatNumber(result.brokerCommission.monthly, 6)}
                  </p>
                  <p>8) Broker daily commission = broker monthly commission / 30</p>
                  <p>
                    = {formatNumber(result.brokerCommission.monthly, 6)} / {result.constants.daysInMonth} ={' '}
                    {formatNumber(result.brokerCommission.daily, 8)}
                  </p>
                </>
              ) : (
                <>
                  <p>6) Broker Available = No, so broker commission is skipped.</p>
                  <p>7) Broker monthly/daily/hourly/minute commission = 0.</p>
                </>
              )}
              <p>9) Net daily investor profit = investor daily profit - broker daily commission</p>
              <p>
                = {formatNumber(result.investorProfit.daily, 6)} - {formatNumber(result.brokerCommission.daily, 8)} ={' '}
                {formatNumber(result.net.daily, 6)}
              </p>
              <p>10) Historical projected net = net daily * active days since start date</p>
              <p>
                = {formatNumber(result.net.daily, 6)} * {result.constants.activeDays} ={' '}
                {formatNumber(result.historicalProjection.net, 4)}
              </p>
              <p>11) Daily table uses moving ROI oscillation between ROI min/max for each day.</p>
              <p>12) Deflection = daily profit from moving ROI - daily profit from average ROI baseline.</p>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-3">Debug Totals</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30">
                <p>Daily Profit Range (from ROI min/max)</p>
                <p className="font-semibold">
                  <DirhamSymbol size={12} inline /> {formatNumber(result.investorProfit.minDaily, 4)} -{' '}
                  <DirhamSymbol size={12} inline /> {formatNumber(result.investorProfit.maxDaily, 4)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30">
                <p>Projected Gross Since Start</p>
                <p className="font-semibold flex items-center gap-1">
                  <DirhamSymbol size={12} inline />
                  {formatNumber(result.historicalProjection.gross, 2)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30">
                <p>Projected Commission Since Start</p>
                <p className="font-semibold flex items-center gap-1">
                  <DirhamSymbol size={12} inline />
                  {formatNumber(result.historicalProjection.commission, 2)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-3">
              Daily Profit Results (Moving ROI Deflection)
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-sm">
                <thead>
                  <tr className="border-b border-primary-200 dark:border-primary-700">
                    <th className="text-left py-2 px-2 font-semibold">Day</th>
                    <th className="text-left py-2 px-2 font-semibold">Date</th>
                    <th className="text-right py-2 px-2 font-semibold">Moving ROI %</th>
                    <th className="text-right py-2 px-2 font-semibold">Daily Profit (AED)</th>
                    <th className="text-right py-2 px-2 font-semibold">Change vs Prev Day</th>
                    <th className="text-right py-2 px-2 font-semibold">Deflection vs Avg ROI</th>
                    {result.investor.brokerAvailable && (
                      <th className="text-right py-2 px-2 font-semibold">Daily Commission (AED)</th>
                    )}
                    <th className="text-right py-2 px-2 font-semibold">Net Daily (AED)</th>
                    <th className="text-right py-2 px-2 font-semibold">Cumulative Profit (AED)</th>
                  </tr>
                </thead>
                <tbody>
                  {result.dailyBreakdown.map((row) => {
                    const positiveDayChange = row.changeVsPreviousDay >= 0;
                    const positiveDeflection = row.deflectionVsAvgROI >= 0;
                    return (
                      <tr
                        key={`${row.date}-${row.dayNumber}`}
                        className="border-b border-primary-100 dark:border-primary-800"
                      >
                        <td className="py-2 px-2">{row.dayNumber}</td>
                        <td className="py-2 px-2">{row.date}</td>
                        <td className="py-2 px-2 text-right">{formatNumber(row.movingROI, 4)}%</td>
                        <td className="py-2 px-2 text-right">{formatNumber(row.dailyProfit, 4)}</td>
                        <td className={`py-2 px-2 text-right ${positiveDayChange ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {row.changeVsPreviousDay > 0 ? '+' : ''}{formatNumber(row.changeVsPreviousDay, 4)}
                        </td>
                        <td className={`py-2 px-2 text-right ${positiveDeflection ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {row.deflectionVsAvgROI > 0 ? '+' : ''}{formatNumber(row.deflectionVsAvgROI, 4)}
                        </td>
                        {result.investor.brokerAvailable && (
                          <td className="py-2 px-2 text-right">{formatNumber(row.dailyCommission, 4)}</td>
                        )}
                        <td className="py-2 px-2 text-right">{formatNumber(row.netDailyProfit, 4)}</td>
                        <td className="py-2 px-2 text-right">{formatNumber(row.cumulativeProfit, 2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

export default AdminROIDebug;
